import { Injectable } from '@angular/core';
import { ListDetailManagementService } from '@app/lists/shared/list-detail-management/services/list-detail-management.service';
import {
  PopoverController,
  ActionSheetController,
  ModalController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState } from '@panamax/app-state';
import { BehaviorSubject } from 'rxjs';
import { HandPricingProductDetails } from 'src/app/order/models/order-view-model';
import { OrderService } from 'src/app/order/services/order.service';
import { ListsOverlayService } from '../../../lists/services/lists-overlay.service';
import { ProductAlternativeSearchService } from '../../../product-detail/services/product-alternative-search.service';
import { ProductDetailTagsService } from '../../../product-detail/services/product-detail-tags.service';
import { NavigationHelperService } from '../../helpers/navigation.helpers.service';
import { EllipsisClickData } from '../../models/usf-product-card.model';
import { ModalService } from '../modal/modal.service';
import { ProductCardService } from './product-card.service';
import { ProductCardAnalyticsService } from './tracking/analytics/product-card-analytics.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { ProductPropertiesEnum } from '@usf/product-types';
import { UsfProductCardModeEnum } from '@shared/constants/usf-product-card-mode.enum';
import { OrderItemService } from '../order-item.service';

@Injectable({
  providedIn: 'root',
})
export class OrderProductCardService extends ProductCardService {
  private handPricingDetails$: BehaviorSubject<HandPricingProductDetails> =
    new BehaviorSubject(null);

  constructor(
    actionSheetController: ActionSheetController,
    modalController: ModalController,
    popoverController: PopoverController,
    panAppState: PanAppState,
    translateService: TranslateService,
    listsOverlayService: ListsOverlayService,
    modalService: ModalService,
    navigationHelperService: NavigationHelperService,
    productAlternativeSearchService: ProductAlternativeSearchService,
    productDetailTagsService: ProductDetailTagsService,
    private orderService: OrderService,
    readonly orderItemService: OrderItemService,
    listDetailManagementService: ListDetailManagementService,
    private productCardAnalyticsService: ProductCardAnalyticsService,
    analyticsService: ProductAnalyticsService,
    toggleNewSearchService: ToggleNewSearchService,
  ) {
    super(
      actionSheetController,
      modalController,
      popoverController,
      panAppState,
      translateService,
      listsOverlayService,
      modalService,
      navigationHelperService,
      productAlternativeSearchService,
      productDetailTagsService,
      listDetailManagementService,
      analyticsService,
      toggleNewSearchService,
    );
  }

  getHandPricingDetails$() {
    return this.handPricingDetails$;
  }

  resetHandPricingDetails() {
    this.handPricingDetails$ = new BehaviorSubject(null);
  }

  constructEllipsisButtons(
    ellipsisClickData: EllipsisClickData,
    originSearchPage?: string,
  ) {
    let buttons = [];
    buttons.push(
      this.getQuickViewButtonWithAnalytcs(ellipsisClickData, originSearchPage),
    );
    if (this.shouldAddProductStatus(ellipsisClickData.product)) {
      buttons.push(this.getProductStatusBtn(ellipsisClickData));
    }
    this.getSellerQuickViewBtn(buttons, ellipsisClickData);
    if (this.shouldAddUpdatePriceBtn(ellipsisClickData)) {
      buttons.push(this.getUpdatePriceBtn(ellipsisClickData));
    }
    if (this.shouldAddRemoveProductBtn(ellipsisClickData)) {
      buttons.push(this.getRemoveProductBtn(ellipsisClickData));
    }
    this.getCompareBtn(buttons, ellipsisClickData);

    return buttons;
  }

  getQuickViewButtonWithAnalytcs(
    ellipsisClickData: EllipsisClickData,
    originSearchPage?: string,
  ) {
    let quickViewButtonWithAnalytics = this.getQuickViewBtn(
      ellipsisClickData,
      originSearchPage,
    );
    quickViewButtonWithAnalytics = {
      ...quickViewButtonWithAnalytics,
      handler: () => {
        this.productCardAnalyticsService.processQuickViewClick(
          ellipsisClickData.product,
        );
        this.openQuickViewModal(
          ellipsisClickData,
          'quickView',
          originSearchPage,
        );
        this.dismissProductCardOptions(ellipsisClickData.isTouch);
      },
    };
    return quickViewButtonWithAnalytics;
  }

  getSearchForSubsBtn(
    ellipsisClickData: EllipsisClickData,
    originSearchPage?: string,
  ) {
    return {
      text: this.translateService.instant('i18n.lists.searchForSubs'),
      handler: () => {
        this.dismissProductCardOptions(ellipsisClickData.isTouch);
        this.orderService.navigateToSearchForSubs(
          ellipsisClickData.product,
          originSearchPage,
        );
      },
    };
  }

  shouldAddUpdatePriceBtn(ellipsisClickData: EllipsisClickData) {
    return ellipsisClickData.allowHandPricing;
  }

  shouldAddRemoveProductBtn(ellipsisClickData: EllipsisClickData) {
    const notOrderable =
      ellipsisClickData?.mode === UsfProductCardModeEnum.reviewOrder &&
      (!ellipsisClickData?.product?.orderable ||
        ellipsisClickData?.product?.summary?.properties?.has(
          ProductPropertiesEnum.specialOrder,
        ) ||
        ellipsisClickData?.product?.notFound ||
        ellipsisClickData?.product?.hideQuantityBoxes);
    return notOrderable;
  }

  getRemoveProductBtn(ellipsisClickData: EllipsisClickData) {
    return {
      text: this.translateService.instant('i18n.orderPage.removeProduct'),
      handler: () => {
        this.dismissProductCardOptions(ellipsisClickData.isTouch);

        this.orderItemService.changeMultiQuantity(
          ellipsisClickData?.product?.productNumber,
          0,
          0,
          undefined,
          false,
          false,
          true,
        );
      },
    };
  }

  getUpdatePriceBtn(ellipsisClickData: EllipsisClickData) {
    return {
      text: this.translateService.instant('i18n.orderPage.updatePrice'),
      handler: () => {
        this.dismissProductCardOptions(ellipsisClickData.isTouch);
        this.handPricingDetails$.next(ellipsisClickData.handPricingDetails);
      },
    };
  }

  routeToPDP(productNumber: number) {
    this.navigationHelperService.routeToProductDetails(productNumber);
  }
}
